import { isCurrencyCode, MAX_AMOUNT_MINOR, MAX_RATE, MIN_RATE, parseRate } from '@split/core';
import { z } from 'zod';

/** Ids are 22-char base64url strings from `generateId()`; clients may also send their own (offline). */
export const idSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{8,64}$/, 'Invalid id (8 to 64 base64url characters)');

/** Currency every new account starts with (users can change it in Settings). */
export const DEFAULT_CURRENCY = 'EUR';

export const currencySchema = z
  .string()
  .refine((v) => isCurrencyCode(v), 'Expected an ISO 4217 code in upper case, e.g. EUR');

/** Safe integer amount in minor units. */
export const minorUnitsSchema = z.int();

/**
 * Amount typed by a user (expense total, payer, exact split entry, payment), at most
 * `MAX_AMOUNT_MINOR` (10^12 minor units) in absolute value.
 */
export const inputMinorUnitsSchema = minorUnitsSchema.min(-MAX_AMOUNT_MINOR).max(MAX_AMOUNT_MINOR);

/** `{ amount, currency }`, amount in integer minor units (cents for EUR, yen for JPY). */
export const moneySchema = z.object({
  amount: minorUnitsSchema,
  currency: currencySchema,
});
export type MoneyDto = z.infer<typeof moneySchema>;

/** Strictly positive user input, at most `MAX_AMOUNT_MINOR`. */
export const positiveMoneySchema = z.object({
  amount: inputMinorUnitsSchema.positive(),
  currency: currencySchema,
});

/** `YYYY-MM-DD`. */
export const isoDateSchema = z.iso.date();
/** ISO 8601 timestamp with `Z`, e.g. `2026-10-12T09:40:00.000Z`. */
export const isoDateTimeSchema = z.iso.datetime();

/** Number of significant digits of a plain decimal string (`"0.00120"` → 2). */
function significantDigits(v: string): number {
  return v.replace('.', '').replace(/^0+/, '').replace(/0+$/, '').length;
}

/**
 * Positive decimal string for exchange rates, e.g. `"1.0823"`: between `MIN_RATE` (1e-9) and
 * `MAX_RATE` (1e9), at most 15 significant digits (stored exactly as typed).
 */
export const rateSchema = z
  .string()
  .max(32)
  .regex(/^\d+(\.\d+)?$/, 'Expected a positive decimal string')
  .refine((v) => significantDigits(v) <= 15, 'At most 15 significant digits')
  .refine((v) => parseRate(v).ok, `Rate must be between ${MIN_RATE} and ${MAX_RATE}`);

export const localeSchema = z.enum(['en', 'es']);
export type Locale = z.infer<typeof localeSchema>;

export const themeSchema = z.enum(['system', 'light', 'dark']);
export type Theme = z.infer<typeof themeSchema>;

/** 8 avatar colours from the design tokens (index into the palette). */
export const avatarColorSchema = z.int().min(0).max(7);

/** A single emoji (grapheme cluster), at most 16 UTF-16 units. */
export const emojiSchema = z.string().trim().min(1).max(16);

export const displayNameSchema = z.string().trim().min(1).max(40);

/** IANA time zone name, e.g. `Europe/Madrid`. Validated with `Intl` on the server. */
export const timezoneSchema = z.string().min(1).max(64);

/** `HH:MM`, 24 h. */
export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM');

/** Opaque pagination cursor. */
export const cursorSchema = z.string().max(200);

export const paginationQuerySchema = z.object({
  cursor: cursorSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export const pageSchema = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    /** Pass as `?cursor=` to get the next page; `null` when there are no more items. */
    nextCursor: z.string().nullable(),
  });

export const okResponseSchema = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof okResponseSchema>;
